<template>
  <v-card style="padding: 16px; border-radius: 16px" class="mx-auto elevation-1">
    <v-card-text>
      <!-- ===== LISTA DE PLANOS ===== -->
      <template v-if="mode === 'list'">
        <!-- Perfil atual em operação (taxas efetivas da PagCom) -->
        <div class="current-profile">
          <div class="current-head">
            <div class="current-head-info">
              <v-icon color="white" class="mr-2">verified</v-icon>
              <div>
                <div class="current-label">Perfil em operação</div>
                <div class="current-name">
                  {{ currentProfileName || "Perfil base PagCom" }}
                </div>
              </div>
            </div>
            <v-btn flat small dark @click="showCurrent = !showCurrent">
              {{ showCurrent ? "Ocultar taxas" : "Ver taxas" }}
            </v-btn>
          </div>

          <div v-if="showCurrent" class="current-body">
            <div
              v-if="currentRates.length === 0"
              class="pa-3"
              style="color: #90a4ae; font-size: 14px"
            >
              Sua conta ainda não tem um perfil base definido. Fale com o seu
              gerente.
            </div>
            <v-tabs
              v-else
              v-model="currentGroup"
              show-arrows
              :slider-color="color"
              class="rate-tabs"
            >
              <v-tab
                v-for="group in groups"
                :key="'ctab-' + group.key"
                class="rate-tab"
              >
                <v-icon small left>{{ group.icon }}</v-icon>
                {{ group.short }}
              </v-tab>
              <v-tab-item v-for="group in groups" :key="'citem-' + group.key">
                <table class="rate-grid">
                  <thead>
                    <tr>
                      <th>{{ group.installment ? "Parcela" : "Taxa" }}</th>
                      <th>Taxa atual</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in currentRatesFor(group.key)"
                      :key="'cr-' + group.key + '-' + row.installment"
                    >
                      <td>
                        {{ group.installment ? row.installment + "x" : group.label }}
                      </td>
                      <td>
                        <b>{{ fmt(row.effective) }}%</b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-tab-item>
            </v-tabs>
          </div>
        </div>

        <v-card-actions class="headline font-weight-bold">
          <h3>Planos de Taxas</h3>
          <v-spacer></v-spacer>
          <v-btn :color="color" dark @click="openNew">
            <v-icon left>add</v-icon> Novo plano
          </v-btn>
        </v-card-actions>

        <div style="font-size: 14px; font-weight: 400; margin-bottom: 16px">
          <i>
            Gerencie seus planos de taxas. Cada plano define o incremento sobre a
            taxa base aplicado aos estabelecimentos matriculados.
          </i>
        </div>

        <div v-if="loading" class="text-xs-center pa-4">
          <v-progress-circular
            indeterminate
            :color="color"
          ></v-progress-circular>
        </div>

        <v-alert v-else-if="plans.length === 0" :value="true" type="info" outline>
          Você ainda não criou nenhum plano de taxas. Clique em
          <b>"Novo plano"</b> para começar.
        </v-alert>

        <table v-else class="plans-table">
          <thead>
            <tr>
              <th>Nome do plano</th>
              <th>Status</th>
              <th class="text-xs-center">Estabelecimentos</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="plan in plans" :key="plan.id">
              <td>
                <b>{{ plan.name }}</b>
              </td>
              <td>
                <span
                  class="status-chip"
                  :class="plan.approved ? 'status-approved' : 'status-pending'"
                >
                  {{ plan.approved ? "Aprovado" : "Em análise" }}
                </span>
              </td>
              <td class="text-xs-center">{{ plan.applied_count }}</td>
              <td class="text-xs-right">
                <v-btn small flat :color="color" @click="editPlan(plan)">
                  <v-icon small left>edit</v-icon> Editar
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <!-- ===== EDITOR DE PLANO ===== -->
      <template v-else>
        <v-card-actions class="headline font-weight-bold">
          <v-btn flat icon :color="color" @click="backToList">
            <v-icon>{{ embedded ? "close" : "arrow_back" }}</v-icon>
          </v-btn>
          <h3>{{ selectedPlanId ? "Editar plano" : "Novo plano" }}</h3>
          <v-spacer></v-spacer>
          <v-btn
            :color="color"
            dark
            :loading="saving"
            :disabled="loading || !baseProfileId"
            @click="save"
          >
            Salvar
          </v-btn>
        </v-card-actions>

        <div style="font-size: 14px; font-weight: 400; margin-bottom: 12px">
          <i>
            Crie planos de taxas definindo o seu incremento sobre a taxa base.
            A taxa final cobrada do estabelecimento é <b>base + seu incremento</b>.
          </i>
        </div>

        <v-layout row wrap align-center>
          <v-flex xs12 md6>
            <v-text-field v-model="name" label="Nome do plano"></v-text-field>
          </v-flex>
        </v-layout>

        <div v-if="loading" class="text-xs-center pa-4">
          <v-progress-circular
            indeterminate
            :color="color"
          ></v-progress-circular>
        </div>

        <v-alert v-else-if="!baseProfileId" :value="true" type="info">
          Sua conta ainda não tem um perfil base definido. Fale com o seu gerente.
        </v-alert>

        <template v-else>
          <v-tabs
            v-model="activeGroup"
            show-arrows
            :slider-color="color"
            class="rate-tabs"
          >
            <v-tab
              v-for="group in groups"
              :key="'tab-' + group.key"
              class="rate-tab"
            >
              <v-icon small left>{{ group.icon }}</v-icon>
              {{ group.short }}
            </v-tab>

            <v-tab-item
              v-for="group in groups"
              :key="'item-' + group.key"
            >
              <div class="rate-tab-head">
                <v-icon :color="color" class="mr-2">{{ group.icon }}</v-icon>
                <span>{{ group.label }}</span>
              </div>
              <table class="rate-grid">
                <thead>
                  <tr>
                    <th>{{ group.installment ? "Parcela" : "Taxa" }}</th>
                    <th>Base</th>
                    <th>Seu incremento</th>
                    <th>Final</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in ratesFor(group.key)"
                    :key="group.key + '-' + row.installment"
                  >
                    <td>
                      {{ group.installment ? row.installment + "x" : group.label }}
                    </td>
                    <td>{{ fmt(row.base) }}%</td>
                    <td>
                      <v-text-field
                        type="number"
                        min="0"
                        step="0.01"
                        v-model.number="row.delta"
                        hide-details
                        single-line
                        suffix="%"
                        style="max-width: 110px"
                      ></v-text-field>
                    </td>
                    <td>
                      <b>{{ fmt(Number(row.base) + Number(row.delta || 0)) }}%</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-tab-item>
          </v-tabs>
        </template>
      </template>
    </v-card-text>

    <v-snackbar v-model="snackbar" :color="snackColor" :timeout="4000">
      {{ snackText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import Flowit from "@/services/flowit";

export default {
  name: "partner-rate-profile",
  props: {
    // Quando embutido (ex.: no cadastro de estabelecimento) abre direto no
    // editor de novo plano, sem a lista nem o card de perfil atual.
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      mode: "list",
      activeGroup: 0,
      loading: true,
      saving: false,
      plans: [],
      selectedPlanId: null,
      name: "",
      baseProfileId: null,
      rates: [],
      currentProfileName: "",
      currentBaseId: null,
      currentRates: [],
      currentGroup: 0,
      showCurrent: true,
      snackbar: false,
      snackText: "",
      snackColor: "success",
      groups: [
        { key: "credit", short: "Crédito", icon: "credit_card", label: "Crédito (parcelado)", installment: true },
        { key: "debit", short: "Débito", icon: "payment", label: "Débito", installment: false },
        { key: "anticipation_automatic", short: "Antec. automática", icon: "flash_on", label: "Antecipação automática", installment: true },
        { key: "anticipation_manual", short: "Antec. manual", icon: "schedule", label: "Antecipação manual", installment: true },
        { key: "pix", short: "PIX", icon: "qr_code", label: "PIX (recebimento)", installment: false },
        { key: "boleto", short: "Boleto", icon: "receipt", label: "Boleto (recebimento)", installment: false },
        { key: "withdraw", short: "Saque", icon: "account_balance_wallet", label: "Saque", installment: false },
      ],
    };
  },
  computed: {
    ...mapGetters({ color: "account/color" }),
  },
  created() {
    if (this.embedded) {
      this.openNew();
    } else {
      this.loadPlans();
      this.loadCurrentProfile();
    }
  },
  methods: {
    loadCurrentProfile() {
      return Flowit.partners
        .rateProfile(null)
        .then((response) => {
          this.currentBaseId = response.data.base_profile_id;
          this.currentProfileName = response.data.base_profile_name || "";
          this.currentRates = response.data.rates || [];
        })
        .catch(() => {});
    },
    currentRatesFor(feeKind) {
      return this.currentRates
        .filter((rate) => rate.fee_kind === feeKind)
        .sort((a, b) => (a.installment || 0) - (b.installment || 0));
    },
    loadPlans() {
      this.loading = true;
      return Flowit.partners
        .rateProfiles()
        .then((response) => {
          this.plans = response.data.rate_profiles || [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.notify("Erro ao carregar os planos.", "error");
        });
    },
    loadProfile(id) {
      this.loading = true;
      return Flowit.partners
        .rateProfile(id)
        .then((response) => {
          this.baseProfileId = response.data.base_profile_id;
          this.name = response.data.rate_profile
            ? response.data.rate_profile.name
            : this.name;
          this.rates = (response.data.rates || []).map((rate) => ({
            ...rate,
            delta: Number(rate.delta) || 0,
          }));
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.notify("Erro ao carregar o plano.", "error");
        });
    },
    openNew() {
      this.mode = "edit";
      this.selectedPlanId = null;
      this.name = "";
      this.loadProfile(null);
    },
    editPlan(plan) {
      this.mode = "edit";
      this.selectedPlanId = plan.id;
      this.name = plan.name;
      this.loadProfile(plan.id);
    },
    backToList() {
      if (this.embedded) {
        this.$emit("close");
        return;
      }
      this.mode = "list";
      this.loadPlans();
    },
    ratesFor(feeKind) {
      return this.rates
        .filter((rate) => rate.fee_kind === feeKind)
        .sort((a, b) => (a.installment || 0) - (b.installment || 0));
    },
    fmt(value) {
      return (Number(value) || 0).toFixed(2);
    },
    save() {
      this.saving = true;
      const payload = {
        rate_profile_id: this.selectedPlanId,
        name: this.name,
        rates: this.rates.map((rate) => ({
          fee_kind: rate.fee_kind,
          installment: rate.installment,
          value: Number(rate.delta) || 0,
        })),
      };
      Flowit.partners
        .saveRateProfile(payload)
        .then((response) => {
          this.saving = false;
          this.notify("Plano de taxas salvo!", "success");
          const savedId = response.data.rate_profile_id;
          this.selectedPlanId = savedId;
          this.loadPlans();
          this.$emit("saved", { id: savedId, name: response.data.name });
        })
        .catch(() => {
          this.saving = false;
          this.notify("Erro ao salvar o plano.", "error");
        });
    },
    notify(text, color) {
      this.snackText = text;
      this.snackColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.rate-grid {
  width: 100%;
  border-collapse: collapse;
}
.rate-grid th,
.rate-grid td {
  text-align: left;
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
}
.rate-grid th {
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.plans-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.plans-table th {
  text-align: left;
  font-size: 12px;
  color: #90a4ae;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 10px 12px;
  border-bottom: 2px solid #eceff1;
}
.plans-table td {
  padding: 12px;
  border-bottom: 1px solid #f2f4f6;
  font-size: 14px;
  color: #37474f;
}
.plans-table tbody tr:hover {
  background: #f7f9fb;
}
.status-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 12px;
  border-radius: 999px;
  white-space: nowrap;
}
.status-approved {
  background: #e8f5e9;
  color: #2e7d32;
}
.status-pending {
  background: #fff3e0;
  color: #ef6c00;
}

.rate-tabs {
  margin-top: 8px;
}
.rate-tab {
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
}
.rate-tab-head {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
  margin: 18px 0 10px;
}

.current-profile {
  border: 1px solid #e3f2fd;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 24px;
}
.current-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #1976d2 0%, #2e9bff 100%);
  color: #fff;
}
.current-head-info {
  display: flex;
  align-items: center;
}
.current-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  opacity: 0.85;
}
.current-name {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
}
.current-body {
  padding: 8px 16px 12px;
  background: #f7f9fb;
}
</style>
